/**
 * Browser-side helpers for photo uploads — shared by the job-files gallery
 * and the quote builder's photo capture.
 */

/** Longest edge we keep for uploaded photos — plenty for a phone, a fraction of the bytes. */
export const MAX_IMAGE_EDGE = 2560;

/** Re-encode an oversized photo as a ≤2560px JPEG. Null = leave the original alone. */
export async function downscaleImage(file: File): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height);
    if (scale >= 1) {
      bitmap.close();
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  } catch {
    // Undecodable here (e.g. HEIC in Chrome) — upload the original bytes.
    return null;
  }
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}
